import { Card } from '@/components/ui/card';
import { getDefaultDateRange } from '@/utils/dashboard-utils';
import { useSearchParams, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { RefreshButton } from '@/components/refresh-button';
import { useState } from 'react';
import { NetworkStatus } from '@apollo/client';
import { slugToMerchantId } from '@/utils/util';
import { formatCurrency } from '@/utils/format-utils';
import { AppActions, AppTitle, AppTitleText } from '@/components/app-header/app-header';
import { ControlledDatePicker } from '@/components/date-picker';
import { DollarSign, TrendingUp, Infinity } from 'lucide-react';
import { useUser } from '@clerk/clerk-react';
import { MetricCard } from '@/routes/dashboards/components/dashboard-metric-card';
import { DashboardSmallCard } from '@/routes/dashboards/components/dashboard-small-card';
import { RevenueMetricsChart } from '@/routes/dashboards/components/revenue-metrics-chart';
import { useDashboardMetrics } from '@/hooks/use-dashboard-metrics';
import { TabType } from '@/routes/dashboards/shared-types';
import classNames from 'classnames';
import { MetricsDataPoint } from '@/routes/dashboards/components/revenue-metrics-chart';
import { useEmqScore } from './hooks/emqScoreHook';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { MetricsTable } from '@/routes/dashboards/components/metrics-table';

export function RevenueMetricsDashboardPage() {
  const { user } = useUser();
  const [searchParams] = useSearchParams();
  const { slug } = useParams<{ slug: string }>();
  const [selectedTab, setSelectedTab] = useState<TabType>('directRevenue');
  const [showEmqPopover, setShowEmqPopover] = useState(false);

  const isEMQScoreDashboardEnabled = useEmqScore();
  let fromDate = searchParams.get('from');
  let toDate = searchParams.get('to');

  if (!fromDate || !toDate || !slug) {
    const defaultDateRange = getDefaultDateRange();
    fromDate = format(defaultDateRange.from, "yyyy-MM-dd'T'00:00:00'Z'");
    toDate = format(defaultDateRange.to, "yyyy-MM-dd'T'00:00:00'Z'");
  }

  const {
    totalEmqScores,
    totalEvents,
    conversionMetrics,
    anonVisitors,
    lifecycleRevenue,
    lifetimeRevenue,
    lifetimeRoi,
    flowsRevenue,
    totalEventsComparison,
    anonVisitorsComparison,
    revenueComparison,
    roiComparison,
    emqScoreAverageAndTrend,
    emqScoreDefaultAndAverage,
    metrics
  } = useDashboardMetrics({
    merchantId: slugToMerchantId(slug!),
    fromDate,
    toDate
  });

  const isRefreshing =
    conversionMetrics.networkStatus === NetworkStatus.refetch ||
    conversionMetrics.loading ||
    anonVisitors.networkStatus === NetworkStatus.refetch ||
    anonVisitors.loading ||
    totalEvents.networkStatus === NetworkStatus.refetch ||
    totalEvents.loading ||
    anonVisitorsComparison.loading ||
    totalEventsComparison.loading ||
    revenueComparison.loading ||
    roiComparison.loading ||
    lifecycleRevenue.networkStatus === NetworkStatus.refetch ||
    lifecycleRevenue.loading ||
    flowsRevenue.networkStatus === NetworkStatus.refetch ||
    flowsRevenue.loading ||
    lifetimeRevenue.loading ||
    lifetimeRoi.loading ||
    totalEmqScores.networkStatus === NetworkStatus.refetch ||
    totalEmqScores.loading ||
    emqScoreDefaultAndAverage.networkStatus === NetworkStatus.refetch ||
    emqScoreDefaultAndAverage.loading;

  const handleRefresh = async () => {
    try {
      await Promise.all([
        conversionMetrics.refetch(),
        anonVisitors.refetch({ refresh: true }),
        totalEvents.refetch({ refresh: true }),
        lifecycleRevenue.refetch({ refresh: true }),
        flowsRevenue.refetch({ refresh: true }),
        roiComparison.refetch({ refresh: true }),
        lifetimeRevenue.refetch({ refresh: true }),
        lifetimeRoi.refetch({ refresh: true }),
        revenueComparison.refetch({ refresh: true }),
        emqScoreAverageAndTrend.refetch(),
        emqScoreDefaultAndAverage.refetch()
      ]);
    } catch (error: unknown) {
      if (error instanceof Error) {
        console.error('Error refetching queries:', error.message);
      } else {
        console.error('Unknown error refetching queries:', error);
      }
    }
  };

  const handleTabChange = (tab: TabType) => {
    setSelectedTab(tab);
  };

  const merchantId = slugToMerchantId(slug!);

  const dashboardSmallCardGridClasses = classNames(
    'grid',
    'gap-4',
    'justify-between',
    'p-4',
    'grid-cols-4'
  );

  const timeSeriesMap: Record<TabType, MetricsDataPoint[]> = {
    anonVisitors: metrics.anonymousVisitors.timeSeries,
    events: metrics.events.timeSeries,
    revenue: metrics.revenue.timeSeries,
    directRevenue: metrics.revenue.timeSeries,
    influencedRevenue: metrics.revenue.timeSeries
  };

  return (
    <div className="space-y-4" key={`dashboard-${merchantId}`}>
      <AppTitle>
        <AppTitleText>Welcome back, {user?.firstName}</AppTitleText>
      </AppTitle>
      <AppActions>
        <RefreshButton onClick={() => void handleRefresh()} isRefreshing={isRefreshing} />
      </AppActions>
      <AppActions>
        <ControlledDatePicker />
      </AppActions>

      <div
        className={classNames('grid gap-4', {
          'grid-cols-3': isEMQScoreDashboardEnabled,
          'grid-cols-2': !isEMQScoreDashboardEnabled
        })}
      >
        <MetricCard
          title="LIFETIME REVENUE"
          value={
            lifetimeRevenue.data?.getLifetimeRevenue
              ? formatCurrency(lifetimeRevenue.data.getLifetimeRevenue)
              : '-'
          }
          icon={<DollarSign size={16} />}
          isLoading={lifetimeRevenue.loading}
          tooltip="This is the sum of influenced and direct revenue since the inception of your account."
        />
        <MetricCard
          title="LIFETIME ROI"
          value={
            typeof lifetimeRoi.data?.getLifetimeROI === 'number'
              ? `${lifetimeRoi.data.getLifetimeROI.toFixed(1)}x`
              : '-'
          }
          icon={<TrendingUp size={16} />}
          isLoading={lifetimeRoi.loading}
          tooltip="This is the ROI of all Contextual provided profiles since your account was created. It is calculated as (lifetime revenue / subscription cost)."
        />
        {isEMQScoreDashboardEnabled && (
          <Popover open={showEmqPopover} onOpenChange={setShowEmqPopover}>
            <PopoverTrigger asChild>
              <div
                onMouseEnter={() => {
                  setShowEmqPopover(true);
                }}
                onMouseLeave={() => {
                  setShowEmqPopover(false);
                }}
              >
                <MetricCard
                  title="META EMQ SCORE"
                  value={
                    emqScoreAverageAndTrend.data?.getEMQScoreAverageAndTrend.avgScore?.toFixed(1) ??
                    '-'
                  }
                  icon={<Infinity size={16} />}
                  isLoading={emqScoreAverageAndTrend.loading}
                />
              </div>
            </PopoverTrigger>
            <PopoverContent
              className="w-auto p-0"
              align="start"
              onMouseEnter={() => {
                setShowEmqPopover(true);
              }}
              onMouseLeave={() => {
                setShowEmqPopover(false);
              }}
            >
              <div className="p-4 border shadow-sm rounded-lg overflow-hidden">
                <MetricsTable
                  headers={['EVENT', 'PRE-CTX', 'CTX AVG']}
                  rows={
                    emqScoreDefaultAndAverage.data?.getEMQScoreDefaultAndAverageForTimePeriod ?? []
                  }
                  compact={true}
                />
              </div>
            </PopoverContent>
          </Popover>
        )}
      </div>

      <Card>
        <div className={dashboardSmallCardGridClasses}>
          <DashboardSmallCard
            key={`direct-revenue-${merchantId}`}
            title="DIRECT REVENUE"
            value={
              flowsRevenue.data?.getFlowsRevenue
                ? formatCurrency(flowsRevenue.data.getFlowsRevenue.aggregate)
                : '-'
            }
            isLoading={flowsRevenue.loading}
            selected={selectedTab === 'directRevenue'}
            onClick={() => {
              handleTabChange('directRevenue');
            }}
            tooltip="Direct revenue from SMS and Email campaigns for the selected date range."
          />
          <DashboardSmallCard
            key={`influenced-revenue-${merchantId}`}
            title="INFLUENCED REVENUE"
            value={
              lifecycleRevenue.data?.getLifecycleRevenue
                ? formatCurrency(lifecycleRevenue.data.getLifecycleRevenue.aggregate)
                : '-'
            }
            isLoading={lifecycleRevenue.loading}
            selected={selectedTab === 'influencedRevenue'}
            onClick={() => {
              handleTabChange('influencedRevenue');
            }}
            tooltip="Influenced revenue from first-party retargeting data sent to Ad Platforms (Meta, TikTok etc.) and AI-powered first party data enrichment for the selected date range."
          />
          <DashboardSmallCard
            key={`anonVisitors-${merchantId}`}
            title="ANON VISITORS RECOGNIZED"
            value={metrics.anonymousVisitors.total}
            isLoading={anonVisitorsComparison.loading || anonVisitors.loading}
            selected={selectedTab === 'anonVisitors'}
            onClick={() => {
              handleTabChange('anonVisitors');
            }}
            tooltip="Number of anonymous visitors recognized in the selected date range"
          />
          <DashboardSmallCard
            key={`events-${merchantId}`}
            title="TOTAL EVENTS"
            value={metrics.events.total}
            isLoading={totalEventsComparison.loading || totalEvents.loading}
            selected={selectedTab === 'events'}
            onClick={() => {
              handleTabChange('events');
            }}
            tooltip="Shows the ratio of Contextual events to combined pixel events (Meta, TikTok, Snapchat). Only active pixel sources (with non-zero events) are included in the calculation."
          />
        </div>
        <div className="p-4">
          <RevenueMetricsChart
            initialTab={selectedTab}
            data={timeSeriesMap[selectedTab]}
            isLoading={
              (selectedTab === 'anonVisitors' && anonVisitors.loading) ||
              (selectedTab === 'events' && totalEvents.loading) ||
              (selectedTab === 'directRevenue' &&
                (lifecycleRevenue.loading || flowsRevenue.loading)) ||
              (selectedTab === 'influencedRevenue' &&
                (lifecycleRevenue.loading || flowsRevenue.loading)) ||
              (selectedTab === 'revenue' && (lifecycleRevenue.loading || flowsRevenue.loading))
            }
          />
        </div>
      </Card>
    </div>
  );
}
